import React, { useMemo } from 'react'
import { styled } from '@mui/material/styles';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useMutation, useQuery } from '@apollo/client'
import { Theme } from 'src/theme'
import { CAREGIVERS, CREATE_CLOCK, PATIENTS } from 'src/apollo/queries'
import { useFormState } from 'src/hooks/useFormState'
import { Autocomplete } from '@mui/lab'
import { eachDayOfInterval, format, isAfter } from 'date-fns'
import { PlusCircle } from 'react-feather'
import ClockList from './ClockList'
import DateRangeComponent from '../DateRangeComponent'
import SlotRow from '../../views/caregiverDetails/Availability/SlotRow'
import CopyToDays from '../../views/caregiverDetails/Availability/CopyToDays'

const PREFIX = 'CreateClockModal';

const classes = {
  root: `${PREFIX}-root`,
  avatar: `${PREFIX}-avatar`,
  avatarContainer: `${PREFIX}-avatarContainer`,
  button: `${PREFIX}-button`,
  bottomButton: `${PREFIX}-bottomButton`,
  dayButton: `${PREFIX}-dayButton`,
  dayButtonSelected: `${PREFIX}-dayButtonSelected`
};

const StyledDialog = styled(Dialog)((
  {
    theme
  }: {
    theme: Theme
  }
) => ({
  [`&.${classes.root}`]: {
    '& .MuiDialog-paper': {
      padding: '15px 25px'
    },
    '& .MuiDialogTitle-root': {
      padding: '5px 0'
    },
    '& .MuiFormControl-root': {
      marginBottom: '15px'
    },
  },

  [`& .${classes.avatar}`]: {
    backgroundColor: theme.palette.primary.main,
    marginRight: '2px'
  },

  [`& .${classes.avatarContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '15px'
  },

  [`& .${classes.button}`]: {
    marginBottom: '10px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.paper,
  },

  [`& .${classes.bottomButton}`]: {
    marginBottom: '10px'
  },

  [`& .${classes.dayButton}`]: {
    border: '1px solid #D0D5DD',
    borderRadius: '50%',
    height: '30px',
    width: '30px',
    padding: 0,
    minWidth: 'unset',
    marginRight: '8px'
  },

  [`& .${classes.dayButtonSelected}`]: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  }
}));

interface TimeSlot {
  timeIn: string;
  timeOut: string;
}

interface DaySlots {
  [key: string]: TimeSlot[];
}

interface ClockModalProps {
  open: boolean,
  setOpen: Function,
  caregiverId?: string,
  patientId?: string,
  date?: { startDate: Date, endDate: Date },
}
const CreateClockModal = (props: ClockModalProps) => {
  const {
    open, setOpen, caregiverId, patientId, date,
  } = props

  const {
    formState, handleValueChange, handleClear
  } = useFormState({
    initialState: {
      values: {
        caregiverId,
        sFPatientId: patientId,
        date: date || {
          startDate: null,
          endDate: null
        },
        note: '',
        selectedDays: [],
        daySlots: {
          MONDAY: [],
          TUESDAY: [],
          WEDNESDAY: [],
          THURSDAY: [],
          FRIDAY: [],
          SATURDAY: [],
          SUNDAY: []
        } as DaySlots
      }
    },
    schema: {
      caregiverId: {
        presence: {
          allowEmpty: false,
          message: 'is required',
        }
      },
      sFPatientId: {
        presence: {
          allowEmpty: false,
          message: 'is required',
        }
      },
      date: {
        presence: {
          allowEmpty: false,
          message: 'is required',
        }
      },
      daySlots: {
        presence: {
          allowEmpty: false,
          message: 'is required',
        }
      }
    }
  })
  interface ClockEntry {
    clockIn: Date;
    clockOut: Date;
    note: string;
    caregiverId: string;
    sFPatientId: string;
    status?: 'success' | 'error';
    message?: string;
  }

  interface ClockStatus {
    index: number;
    status: 'success' | 'error';
    error?: string;
  }

  const [clocksToCreate, setClocksToCreate] = React.useState<ClockEntry[]>([])
  const [clockStatuses, setClockStatuses] = React.useState<ClockStatus[]>([])
  const [patientSearch, setPatientSearch] = React.useState('')
  const [caregiverSearch, setCaregiverSearch] = React.useState('')

  const [createClock, { loading }] = useMutation(CREATE_CLOCK, {
    refetchQueries: ['getCaregiverClocks']
  })

  const { data: patientData } = useQuery(PATIENTS, {
    variables: {
      data: {
        search: patientSearch,
        take: 40,
        skip: 0,
        caregiverId: formState.values.caregiverId,
        patientId: formState.values.sFPatientId
      }
    },
  })

  const { data: caregiverData } = useQuery(CAREGIVERS, {
    variables: {
      data: {
        search: caregiverSearch,
        take: 40,
        skip: 0,
        patientId: formState.values.sFPatientId,
        caregiverId: formState.values.caregiverId,
      }
    },
  })
  const handleClose = () => { handleClear(); setClocksToCreate([]); setOpen(false); }

  const isFutureDate = useMemo(() => {
    const startDate = formState.values.date?.startDate ? new Date(formState.values.date.startDate) : null
    const endDate = formState.values.date?.endDate ? new Date(formState.values.date.endDate) : null
    if (!startDate && !endDate) return false

    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)

    return (startDate && isAfter(startDate, tomorrow)) ||
      (endDate && isAfter(endDate, tomorrow))
  }, [formState.values.date])

  const hasTimeSlots = Object.values(formState.values.daySlots).some((slots) => slots.length > 0);
  // Only check if we have valid form data and at least one time slot
  const isValid = !isFutureDate && hasTimeSlots && formState.values.caregiverId && formState.values.sFPatientId && formState.values.date

  const handleSave = async () => {
    if (!formState.values.date?.startDate || !formState.values.date?.endDate) return;

    const clocks: any[] = [];
    const { date, note, caregiverId, sFPatientId, daySlots } = formState.values;

    eachDayOfInterval({ start: date.startDate, end: date.endDate })
      .forEach((currentDate) => {
        const dayName = format(currentDate, 'EEEE').toUpperCase();
        const slots = daySlots[dayName] || [];

        slots.forEach((slot) => {
          const [inHours, inMinutes] = slot.timeIn.split(':');
          const [outHours, outMinutes] = slot.timeOut.split(':');

          const clockIn = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
            parseInt(inHours, 10),
            parseInt(inMinutes, 10)
          );

          const clockOut = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
            parseInt(outHours, 10),
            parseInt(outMinutes, 10)
          );

          clocks.push({
            clockIn,
            clockOut,
            note,
            caregiverId,
            sFPatientId
          });
        });
      });

    const clockPromises = clocks.map((clock, index) => createClock({ variables: { data: clock } })
      .then(() => ({
        clock,
        status: { index, status: 'success' as const },
        result: { ...clock, status: 'success' as const, message: 'Clock created successfully' }
      }))
      .catch((error: any) => ({
        clock,
        status: { index, status: 'error' as const, error: error?.message || 'Unknown error' },
        result: { ...clock, status: 'error' as const, message: error?.message || 'Unknown error' }
      })));

    const clockResults = await Promise.all(clockPromises);
    const results = clockResults.map((r) => r.result);
    const statuses = clockResults.map((r) => r.status);

    setClocksToCreate(results);
    setClockStatuses(statuses);
  };

  React.useEffect(() => {
    if (caregiverData && !caregiverData?.caregivers?.results?.some((c) => c?.id === formState.values?.caregiverId)) handleValueChange('caregiverId', '')
    if (caregiverData?.caregivers?.results?.length === 1) handleValueChange('caregiverId', caregiverData?.caregivers?.results?.[0]?.id)
  }, [caregiverData])

  React.useEffect(() => {
    if (!formState.values.caregiverId) handleValueChange('caregiverId', caregiverId)
  }, [caregiverId])

  React.useEffect(() => {
    if (patientData && !patientData?.patients?.results?.some((p) => p?.id === formState.values?.sFPatientId)) handleValueChange('sFPatientId', '')
    if (patientData?.patients?.results?.length === 1) handleValueChange('sFPatientId', patientData?.patients?.results?.[0]?.id)
  }, [patientData])

  // Automatically set selectedDays based on which days have time slots
  React.useEffect(() => {
    const daysWithSlots = Object.entries(formState.values.daySlots)
      .filter(([_, slots]) => slots.length > 0)
      .map(([day]) => day)
    handleValueChange('selectedDays', daysWithSlots)
  }, [formState.values.daySlots])

  return (
    <StyledDialog className={classes.root} onClose={handleClose} open={open} maxWidth="md">
      <Box width="600px">
        <DialogTitle id="simple-dialog-title" sx={{ pb: 0 }}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box display="flex" style={{ backgroundColor: '#eceff1', borderRadius: '12px' }} gap={2} p={2} pt={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">Patient</Typography>
            <Autocomplete
              id="patient"
              options={patientData?.patients?.results || []}
              freeSolo
              size="small"
              fullWidth
              style={{
                backgroundColor: 'white', maxHeight: 40, minWidth: 240
              }}
              value={patientData?.patients?.results?.find((p) => p?.id === formState.values?.sFPatientId) || null}
              onChange={(e, v) => {
                if (v === null) {
                  setPatientSearch('')
                  handleValueChange('sFPatientId', '')
                  handleValueChange('caregiverId', '')
                }
                handleValueChange('sFPatientId', v?.id || undefined);
              }}
              getOptionLabel={(option: any) => `${option?.firstName || ''} ${option?.lastName || ''}`}
              renderInput={(params) => (
                <TextField
                  size="small"
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                  }}
                  {...params}
                  placeholder="Select Patient"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6" style={{ marginLeft: 2 }}>Caregiver</Typography>
            <Autocomplete
              id="caregiver"
              options={caregiverData?.caregivers?.results || []}
              freeSolo
              size="small"
              fullWidth
              style={{
                backgroundColor: 'white', maxHeight: 40, marginLeft: 2, minWidth: 240
              }}
              value={caregiverData?.caregivers?.results?.find((c) => c?.id === formState.values?.caregiverId)
                || null}
              onChange={(e, v) => {
                handleValueChange('caregiverId', v?.id || undefined)
                if (v === null) {
                  setCaregiverSearch('')
                  handleValueChange('sFPatientId', '')
                }
              }}
              getOptionLabel={(option: any) => (
                `${option?.sfContact?.firstName || option.firstName || ''} ${option?.sfContact?.lastName || option.lastName || ''}`
              )}
              renderInput={(params) => (
                <TextField
                  value={caregiverSearch}
                  onChange={(e) => setCaregiverSearch(e.target.value)}
                  size="small"
                  {...params}
                  placeholder="Select Caregiver"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </Box>
        <DialogContent>
          {clocksToCreate.length > 0 ? (
            <ClockList
              clocks={clocksToCreate}
              statuses={clockStatuses}
              updateClocksToCreate={({ index, value }) => {
                const newClocks = [...clocksToCreate];
                newClocks[index] = value;
                setClocksToCreate(newClocks);
              }}
            />
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                  Select Date Range
                </Typography>
                <DateRangeComponent
                  error={isFutureDate}
                  timePeriodOptions={[]}
                  selectionRange={formState.values.date || { startDate: null, endDate: null }}
                  allowClearable
                  helperText={isFutureDate ? 'Date cannot be in the future' : ''}
                  placeholder="Select date / range"
                  style={{ margin: '0', minWidth: '340px', width: '340px' }}
                  setSelectionRange={(value) => handleValueChange('date', value)}
                  onTimePeriodChange={(e, value) => handleValueChange('date', value)}
                />
              </Box>
              <Box mb={2}>
                {Object.entries(formState.values.daySlots).map(([day, slots]) => (
                  <Box key={day} display="flex" alignItems="flex-start">
                    <Box display="flex" paddingTop="5px">
                      {slots.length > 0 ? (
                        <Box
                          bgcolor="#7F56D9"
                          borderRadius="50%"
                          height="30px"
                          width="30px"
                          justifyContent="center"
                          display="flex"
                          alignItems="center"
                          marginRight={1}
                        >
                          <Typography className="capitalize" color="white" fontWeight={600}>
                            {day.charAt(0)}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          border="1px solid #D0D5DD"
                          borderRadius="50%"
                          height="30px"
                          width="30px"
                          justifyContent="center"
                          display="flex"
                          alignItems="center"
                          marginRight={1}
                        >
                          <Typography className="capitalize" color="#475467" fontWeight={600}>
                            {day.charAt(0)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <SlotRow
                      small
                      fields={slots.map((slot, index) => ({
                        id: index,
                        originalIndex: index,
                        timeIn: slot.timeIn,
                        timeOut: slot.timeOut
                      }))}
                      remove={(index) => {
                        const newSlots = [...slots];
                        newSlots.splice(index, 1);
                        const newDaySlots = { ...formState.values.daySlots };
                        newDaySlots[day] = newSlots;
                        handleValueChange('daySlots', newDaySlots);
                      }}
                      errors={{}}
                      fieldName={`daySlots.${day}`}
                      setValue={(path, value) => {
                        const [, index, field] = path.match(/\[(\d+)\]\.(\w+)/) || [];
                        if (index !== undefined && field) {
                          const newSlots = [...slots];
                          newSlots[parseInt(index, 10)] = {
                            ...newSlots[parseInt(index, 10)],
                            [field === 'startTime' ? 'timeIn' : 'timeOut']: value
                          };
                          const newDaySlots = { ...formState.values.daySlots };
                          newDaySlots[day] = newSlots;
                          handleValueChange('daySlots', newDaySlots);
                        }
                      }}
                      watch={(path) => {
                        const [, index, field] = path.match(/\[(\d+)\]\.(\w+)/) || [];
                        if (index !== undefined && field) {
                          const slot = slots[parseInt(index, 10)];
                          return field === 'startTime' ? slot?.timeIn : slot?.timeOut;
                        }
                        return '';
                      }}
                    />
                    <Box display="flex" paddingTop="4px">
                      <IconButton
                        onClick={() => {
                          const newSlots = [...slots];
                          newSlots.push({ timeIn: '', timeOut: '' });
                          const newDaySlots = { ...formState.values.daySlots };
                          newDaySlots[day] = newSlots;
                          handleValueChange('daySlots', newDaySlots);
                        }}
                      >
                        <PlusCircle color="#475467" size="18px" />
                      </IconButton>
                      {slots.length > 0 && (
                        <CopyToDays
                          small
                          day={day}
                          name="daySlots"
                          watch={(path) => {
                            if (path === 'daySlots') {
                              return Object.entries(formState.values.daySlots).map(([day, slots]) => (
                                slots.map((slot) => ({
                                  day,
                                  startTime: slot.timeIn,
                                  endTime: slot.timeOut
                                }))
                              )).flat();
                            }
                            return [];
                          }}
                          append={(newSlots) => {
                            const newDaySlots = { ...formState.values.daySlots };
                            newSlots.forEach((slot) => {
                              const { day, startTime, endTime } = slot;
                              if (!newDaySlots[day]) newDaySlots[day] = [];
                              newDaySlots[day].push({
                                timeIn: startTime,
                                timeOut: endTime
                              });
                            });
                            handleValueChange('daySlots', newDaySlots);
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
              <TextField
                value={formState.values.note}
                multiline
                size="small"
                label="Note"
                fullWidth
                variant="outlined"
                onChange={(e) => handleValueChange('note', e.target.value)}
                sx={{ mt: 2 }}
              />
              {loading && <CircularProgress size={20} />}
            </>
          )}
          {!clocksToCreate.length && (
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  color: '#344054',
                  borderColor: '#D0D5DD',
                  '&:hover': {
                    borderColor: '#D0D5DD',
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={loading || !isValid}
                onClick={handleSave}
                sx={{
                  backgroundColor: '#7F56D9',
                  '&:hover': {
                    backgroundColor: '#6941C6'
                  }
                }}
              >
                Save
              </Button>
            </DialogActions>
          )}
        </DialogContent>
      </Box>
    </StyledDialog>
  );
}

export default CreateClockModal;
